import { Injectable } from '@angular/core';
import { PermissionService } from './permission.service';
import { JwtUtils } from '../@core/utils/jwt.utils';
import { AuthQuery } from '../auth/auth.query';

@Injectable({
  providedIn: 'root'
})
export class AdminPermissionService {

  constructor(
    private permissionService: PermissionService,
    private authQuery: AuthQuery
  ) {}

  /**
   * Check if current user has admin privileges
   */
  isAdmin(): boolean {
    // First try the permission service
    const currentUser = this.permissionService.getCurrentUser();
    if (currentUser) {
      const adminPermissions = [
        'is_admin',
        'is_superuser', 
        'is_compadmin',
        'admin_access',
        'view_passwords'
      ];
      if (this.permissionService.hasAnyPermission(adminPermissions)) {
        return true;
      }
    }

    // Fallback to JWT token check
    if (this.isAdminFromToken()) {
      return true;
    }

    // Fallback to auth store role check
    if (this.isAdminFromAuthStore()) {
      return true;
    }

    return false;
  }

  /**
   * Check admin status from auth store
   */
  private isAdminFromAuthStore(): boolean {
    try {
      const authState = this.authQuery.getValue();
      if (!authState) {
        return false;
      }

      // Check if role indicates admin (role 1 = SYSADMIN, role 2 = COMPADMIN)
      if (authState.role === 1 || authState.role === 2) {
        return true;
      }

      // Check if userid indicates admin (you might have specific admin user IDs)
      // This is a fallback - adjust based on your system
      if (authState.userid === 1 || authState.userid === 11) {
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error checking admin status from auth store:', error);
      return false;
    }
  }

  /**
   * Check if user can view passwords specifically
   */
  canViewPasswords(): boolean {
    return this.isAdmin() || this.permissionService.hasPermission('view_passwords');
  }

  /**
   * Check if user has any admin role
   */
  hasAnyAdminRole(): boolean {
    return this.isAdmin();
  }

  /**
   * Check if user has specific admin role
   */
  hasAdminRole(role: 'is_admin' | 'is_superuser' | 'is_compadmin'): boolean {
    return this.permissionService.hasPermission(role);
  }

  /**
   * Get current user's admin roles
   */
  getAdminRoles(): string[] {
    const currentUser = this.permissionService.getCurrentUser();
    if (!currentUser) {
      return [];
    }

    const adminRoles = ['is_admin', 'is_superuser', 'is_compadmin', 'admin_access', 'view_passwords'];
    return currentUser.permissions.filter(permission => adminRoles.includes(permission));
  }

  /**
   * Check admin status from JWT token (fallback method)
   */
  isAdminFromToken(): boolean {
    const token = localStorage.getItem('access_token');
    if (!token) {
      return false;
    }

    try {
      const decoded = JwtUtils.decodeToken(token);
      if (!decoded) {
        return false;
      }

      // Check for admin permissions
      const permissions = decoded.permissions || [];
      const adminPermissions = ['is_admin', 'is_superuser', 'is_compadmin', 'admin_access', 'view_passwords'];
      
      if (adminPermissions.some(permission => permissions.includes(permission))) {
        return true;
      }

      // Check for admin role_id (1 = SYSADMIN, 2 = COMPADMIN)
      // Note: The JWT might not contain role_id, but we can check if it exists
      if (decoded.role_id && (decoded.role_id === 1 || decoded.role_id === 2)) {
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error decoding token for admin check:', error);
      return false;
    }
  }

  /**
   * Get admin status with fallback methods
   */
  getAdminStatus(): { isAdmin: boolean; method: string; roles: string[]; debug: any } {
    const debug = {
      permissionService: this.permissionService.getCurrentUser(),
      authStore: this.authQuery.getValue(),
      token: localStorage.getItem('access_token') ? 'exists' : 'missing'
    };

    // Try permission service first
    if (this.isAdmin()) {
      return {
        isAdmin: true,
        method: 'permission_service',
        roles: this.getAdminRoles(),
        debug: debug
      };
    }

    // Fallback to token check
    if (this.isAdminFromToken()) {
      const token = localStorage.getItem('access_token');
      const decoded = token ? JwtUtils.decodeToken(token) : null;
      const roles = decoded?.permissions?.filter((p: string) => 
        ['is_admin', 'is_superuser', 'is_compadmin', 'admin_access', 'view_passwords'].includes(p)
      ) || [];

      return {
        isAdmin: true,
        method: 'jwt_token',
        roles: roles,
        debug: debug
      };
    }

    return {
      isAdmin: false,
      method: 'none',
      roles: [],
      debug: debug
    };
  }

  /**
   * Debug method to help troubleshoot admin status
   */
  debugAdminStatus(): void {
    const status = this.getAdminStatus();
    console.log('Admin Status Debug:', status);
    
    // Also log individual checks
    console.log('Permission Service User:', this.permissionService.getCurrentUser());
    console.log('Auth Store State:', this.authQuery.getValue());
    
    const token = localStorage.getItem('access_token');
    if (token) {
      const decoded = JwtUtils.decodeToken(token);
      console.log('JWT Token Decoded:', decoded);
    } else {
      console.log('No access token found');
    }
  }
}
